import QRCode from 'react-qr-code';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AR_VIEWER_ROUTE } from '../../constants/routePathConstants';
import {
  apiCreatePublicUUID,
  apiGetPublicUUID,
  apiDeletePublicUUID,
  apiGetPublicUUIDPublic,
  apiCreatePublicUUIDPublic,
  apiGetProp,
  apiGetPropPublicInfo
} from '../../helpers/api';
import { isMobile } from 'react-device-detect';
import icons from '../../assets';
import Button from '../../components/Button';
import Loader from '../Loader';
import { logDebug, logWarn } from './arDebugLog';

function ARQRcode({
  propId,
  propName,
  isPublicPage,
  className = '',
  loading,
  orgProductUniqueCodeId = null,
  showLoading = false,
  usdzUrl = null,     
  isRoofProp = false, 
  customButtonText = null,
  customButtonStyle = null,
  qrCodeSize = '',
  productUnicodeId = null
}) {
  const { t } = useTranslation('common');
  const [publicUUID, setPublicUUID] = useState(false);
  const [arLoading, setArLoading] = useState(false); 
  // Internal state for product details fetched via PROPS_API
  const [internalUsdzUrl, setInternalUsdzUrl] = useState(null);
  const [internalIsRoofProp, setInternalIsRoofProp] = useState(false);
  
  useEffect(() => {
    if (orgProductUniqueCodeId && !publicUUID) {
      setPublicUUID(orgProductUniqueCodeId);
    }
  }, [orgProductUniqueCodeId]);

  const { ARViewerIcon } = icons;

  const searchParams = new URLSearchParams(window.location.search);
  const langParam = searchParams.get('lang');
  const langSuffix = langParam ? `?lang=${langParam}` : '';
  const BASE_URL = `${window.location.origin}${AR_VIEWER_ROUTE}`;
  const AR_VIEWER_URL = `${BASE_URL}?prop-id=${propId}${langParam ? `&lang=${langParam}` : ''}`;

  // Fetch Product Details from PROPS_API
  const fetchProductDetails = async (id, uuid) => {
    if(isPublicPage && !uuid){
      if(!productUnicodeId && typeof isPublicPage === 'string'){
        uuid = isPublicPage
      }else{
       return
      }
    }else if(!id){
      return
    }
    try {
      // Using your existing helper pattern:
      const api = isPublicPage ? apiGetPropPublicInfo : apiGetProp;
      const response = await api(isPublicPage ? uuid : id);
      if (response) {
        const data = response;
        setInternalUsdzUrl(data?.usdz_file || null);
        data?.is_roof_prop && setInternalIsRoofProp(data?.is_roof_prop || false);
      }
    } catch (e) {
      console.error("Failed to fetch product details for AR:", e);
    }
  };

  const handleToggleChange = async () => {
    if (!publicUUID) {
      const formData = new FormData();
      formData.append('product', propId);
      const api = isPublicPage ? apiCreatePublicUUIDPublic : apiCreatePublicUUID;
      const response = await api(formData);
      if (response?.status) {
        setPublicUUID(response?.data?.unique_code);
        // UUID created, now fetch details to ensure we have USDZ info
        fetchProductDetails(propId,response?.data?.product_details?.unicode);
        setInternalIsRoofProp(response?.data?.product_details?.is_roof_prop || false);
      }
    } else {
      const response = await apiDeletePublicUUID(publicUUID);
      if (response?.status) setPublicUUID('');
    }
  };

  const fetchARAPI = async () => {
    logDebug('ARQRcode', 'fetchARAPI:start', { propId, isPublicPage });
    showLoading && setArLoading(true);
    try {
      const api = isPublicPage ? apiGetPublicUUIDPublic : apiGetPublicUUID;
      const response = await api(`?product=${propId}`);
      logDebug('ARQRcode', 'fetchARAPI:response', { count: response?.results?.length });

      if (response?.results?.length) {
        setPublicUUID(response.results[0]?.unique_code);
        await fetchProductDetails(propId,response.results[0]?.product_details?.unicode);
          setInternalIsRoofProp(response.results[0]?.product_details?.is_roof_prop || false);
      } else {
        handleToggleChange();
      }
    } catch (e) {
      logWarn('ARQRcode', 'fetchARAPI:failed', { message: e?.message || String(e) });
    } finally {
      showLoading && setArLoading(false);
      logDebug('ARQRcode', 'fetchARAPI:end');
    }
  }

  useEffect(async () => {
    // Logic: If we are missing the USDZ url (from props), we need to go fetch it.
    // This runs fetchARAPI which now includes the call to fetchProductDetails.
    if (!orgProductUniqueCodeId && !loading && propId) {
      fetchARAPI()
    } else if (propId && !usdzUrl) {
       fetchARAPI()
    }
  }, [loading, propId]);

  // Determine effective values
  const effectiveUsdz = usdzUrl || internalUsdzUrl;
  const effectiveIsRoof = isRoofProp || internalIsRoofProp;

  const getQRValue = () => {
    let origin = window.location.origin;

    const params = new URLSearchParams();
    if (propId) params.set('prop-id', propId);
    if (publicUUID) params.set('token', publicUUID);
    params.set('type', 'ar');
    // Propagate ?debug=1 across the QR / open-link redirect so mobile users
    // keep the debug panel after the ar-redirect hop.
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('debug') === '1') params.set('debug', '1');
    } catch (_) {}

    let url;
    if (effectiveIsRoof) {
      url = `${origin}/ceilingclip?${params.toString()}`;
    } else if (publicUUID) {
      const suffix = params.get('debug') ? `&debug=1` : '';
      url = `${origin}/ar-redirect/${publicUUID}?type=ar${suffix}`;
    } else {
      url = `${origin}/ar-redirect/?${params.toString()}`;
    }
    logDebug('ARQRcode', 'getQRValue', { url });
    return url;
  };

  return (
    <div
      className={`d-flex flex-column gap-2 ${className}`}
      role="img"
      aria-label={`QR code for ${propName || 'AR viewer'}`}>
      {showLoading && arLoading ? <Loader loading={true} isV2={true} bgOpacity={0.75} /> : null}
      <QRCode
        value={getQRValue()}
        title={propName || ''}
        level="Q"
        role="img"
        aria-label={`QR code to view ${propName || 'product'} in AR`}
        {...(qrCodeSize ? { style: { width: qrCodeSize, height: qrCodeSize } } : { size: 150 })}
      />
      
      {isMobile ? (
        <Button
          className={`${customButtonStyle?.color ? '' : 'lf-btn-1 text-white'} ${customButtonStyle?.padding ? 'custom-button-padding' : ''}`}
          iconBefore={<ARViewerIcon aria-hidden="true" />}
          onClick={() => window.open(getQRValue())}
          aria-label={`Open AR viewer for ${propName || 'product'} in new window`}
          style={
            customButtonStyle?.color
              ? {
                  backgroundColor: 'var(--primary-color)',
                  borderColor: 'var(--primary-color)',
                  ...customButtonStyle,
                  '--button-font-size': customButtonStyle?.fontSize,
                  '--button-padding': customButtonStyle?.padding,
                  ...(customButtonStyle?.padding ? { height: 'auto' } : {}),
                }
              : customButtonStyle
                ? {
                    ...customButtonStyle,
                    '--button-font-size': customButtonStyle?.fontSize,
                    '--button-padding': customButtonStyle?.padding,
                    ...(customButtonStyle?.padding ? { height: 'auto' } : {}),
                  }
                : {}
          }
          textClassName={customButtonStyle?.fontSize ? 'custom-button-font-size' : ''}>
          {customButtonText || t('open_link')}
        </Button>
      ) : null}
    </div>
  );
}

export default ARQRcode;
